import { isTenantExpCA, isTenantExpInService, isTenantExpUS } from '@tenants';
import { getTenantById } from '@tenants/manager';
import { SiteLanguage } from 'constants/locale';
import { IUserContext, UserContext } from 'contexts';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'next-i18next';
import { CountryCodeList } from 'types/countries';
import { useTenantId } from './use-tenant-id';
import { LOCALE_MODAL_PATHS } from 'types/locales';
import { Namespaces } from 'types/locales';

// Default values for consent labels
const DEFAULT_PRIVACY_POLICY_TEXT = 'By clicking "{{buttonText}}", you agree to our Privacy Policy and Terms of Use.';
const DEFAULT_PRIVACY_POLICY_WITH_LINKS = 'By clicking "{{buttonText}}", you agree to our <privacyPolicyLink>Privacy Policy</privacyPolicyLink> and <termsOfUseLink>Terms of Use</termsOfUseLink>.';
const DEFAULT_MARKETING_CONSENT_TEXT = 'By clicking "{{buttonText}}", you consent to receive marketing communications.';
const DEFAULT_MARKETING_CONSENT_EXP_TEXT = 'By clicking "{{buttonText}}", you consent to receive marketing communications from eXp Realty.';
const DEFAULT_MARKETING_CONSENT_ZOOCASA_TEXT = 'By clicking "{{buttonText}}", you consent to receive marketing communications from Zoocasa.';

const useConsentLabels = (buttonText: string, countryCodeOverride?: CountryCodeList) => {
  const tenantId = useTenantId();
  const tenant = getTenantById(tenantId);
  const { userLocation } = useContext(UserContext) as IUserContext;
  const { t, i18n } = useTranslation(Namespaces.MODAL);

  const isExpTheme = isTenantExpCA(tenantId) || isTenantExpUS(tenantId);
  const countryCode = countryCodeOverride || userLocation?.countryCode;
  const isUserLocationInCanada = countryCode == CountryCodeList.CANADA;

  const privacyPolicyUrl = tenant.theme.privacyPolicy(i18n.language as SiteLanguage);
  const termsOfUseUrl = tenant.theme.termsOfUse(i18n.language as SiteLanguage);

  const PRIVACY_LABEL_PLAINTEXT_DEFAULT = useMemo(
    () => t(LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_PRIVACY_POLICY_DEFAULT, { 
      buttonText, 
      defaultValue: DEFAULT_PRIVACY_POLICY_TEXT, 
    }),
    [t, buttonText]
  );

  const PRIVACY_LABEL_PLAINTEXT_EXP_OUTSIDE_CANADA = useMemo(
    () => t(LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_PRIVACY_POLICY_EXP_OUTSIDE_CANADA, { 
      buttonText, 
      defaultValue: DEFAULT_PRIVACY_POLICY_TEXT, 
    }),
    [t, buttonText]
  );

  const PRIVACY_LABEL_DEFAULT = useMemo(() => (
    <p>
      <Trans
        i18nKey={LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_PRIVACY_POLICY_DEFAULT}
        values={{ buttonText }}
        defaults={DEFAULT_PRIVACY_POLICY_WITH_LINKS}
        components={{
          privacyPolicyLink: <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer" />,
          termsOfUseLink: <a href={termsOfUseUrl} target="_blank" rel="noopener noreferrer" />,
        }}
      />
    </p>
  ), [buttonText, privacyPolicyUrl, termsOfUseUrl]);

  const PRIVACY_LABEL_EXP_OUTSIDE_CANADA = useMemo(() => (
    <p>
      <Trans
        i18nKey={LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_PRIVACY_POLICY_EXP_OUTSIDE_CANADA}
        values={{ buttonText }}
        defaults={DEFAULT_PRIVACY_POLICY_WITH_LINKS}
        components={{
          privacyPolicyLink: <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer" />,
          termsOfUseLink: <a href={termsOfUseUrl} target="_blank" rel="noopener noreferrer" />,
        }}
      />
    </p>
  ), [buttonText, privacyPolicyUrl, termsOfUseUrl]);

  const MARKETING_LABEL_DEFAULT = useMemo(() => t(LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_MARKETING_DEFAULT, { 
    buttonText, 
    defaultValue: DEFAULT_MARKETING_CONSENT_TEXT, 
  }), [t, buttonText]);

  const MARKETING_LABEL_EXP_OUTSIDE_CANADA = useMemo(() => t(LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_MARKETING_EXP_OUTSIDE_CANADA, { 
    buttonText, 
    defaultValue: DEFAULT_MARKETING_CONSENT_EXP_TEXT, 
  }), [t, buttonText]);

  const MARKETING_LABEL_ZOOCASA_OUTSIDE_CANADA = useMemo(() => t(LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_MARKETING_ZOOCASA_OUTSIDE_CANADA, { 
    buttonText, 
    defaultValue: DEFAULT_MARKETING_CONSENT_ZOOCASA_TEXT, 
  }), [t, buttonText]);

  const MARKETING_LABEL_EXP_IN_SERVICE = useMemo(() => t(LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_MARKETING_EXP_IN_SERVICE, { 
    buttonText, 
    defaultValue: DEFAULT_MARKETING_CONSENT_EXP_TEXT, 
  }), [t, buttonText]);

  const [privacyPolicyPlainText, setPrivacyPolicyPlainText] = useState<string>(PRIVACY_LABEL_PLAINTEXT_DEFAULT);
  const [privacyPolicyConsentLabel, setPrivacyPolicyConsentLabel] = useState<JSX.Element>(PRIVACY_LABEL_DEFAULT);
  const [marketingCommunicationsConsentLabel, setMarketingCommunicationsConsentLabel] = useState(MARKETING_LABEL_DEFAULT);

  useEffect(() => {
    if (isUserLocationInCanada && !isTenantExpInService(tenantId)) {
      // Right now the app is configured to NOT display any consent text for users within Canada and non Inservice tenant, so we'll default to Zoocasa consent text
      setPrivacyPolicyConsentLabel(PRIVACY_LABEL_DEFAULT);
      setPrivacyPolicyPlainText(PRIVACY_LABEL_PLAINTEXT_DEFAULT);
      setMarketingCommunicationsConsentLabel(MARKETING_LABEL_DEFAULT);
    } else {
      if (isExpTheme) {
        // Show eXp specific consent text to eXp users outside Canada
        setPrivacyPolicyConsentLabel(PRIVACY_LABEL_EXP_OUTSIDE_CANADA);
        setPrivacyPolicyPlainText(PRIVACY_LABEL_PLAINTEXT_EXP_OUTSIDE_CANADA);
        setMarketingCommunicationsConsentLabel(MARKETING_LABEL_EXP_OUTSIDE_CANADA);
      } else if (isTenantExpInService(tenantId)) {
        setMarketingCommunicationsConsentLabel(MARKETING_LABEL_EXP_IN_SERVICE);
      } else {
        // Show Zoocasa specific consent text to non-Canadian users
        setPrivacyPolicyConsentLabel(PRIVACY_LABEL_DEFAULT);
        setPrivacyPolicyPlainText(PRIVACY_LABEL_PLAINTEXT_DEFAULT);
        setMarketingCommunicationsConsentLabel(MARKETING_LABEL_ZOOCASA_OUTSIDE_CANADA);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpTheme,
    isUserLocationInCanada,
    buttonText,
    PRIVACY_LABEL_DEFAULT,
    PRIVACY_LABEL_EXP_OUTSIDE_CANADA,
    PRIVACY_LABEL_PLAINTEXT_DEFAULT,
    PRIVACY_LABEL_PLAINTEXT_EXP_OUTSIDE_CANADA,
    MARKETING_LABEL_DEFAULT,
    MARKETING_LABEL_EXP_OUTSIDE_CANADA,
    MARKETING_LABEL_ZOOCASA_OUTSIDE_CANADA,
  ]);

  const fubConsentDisclaimerText = `\n"${privacyPolicyPlainText}"\n"${marketingCommunicationsConsentLabel}"`;

  return { privacyPolicyConsentLabel, fubConsentDisclaimerText, marketingCommunicationsConsentLabel };

};

export { useConsentLabels };